import { useState } from 'react';
import type { FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CalendarRole } from '@cerebro-diem/core';
import { errorMessage } from '../../services/errors';
import { caldavCalendars, caldavConnection, caldavKeys, connectCaldav, disconnectCaldav, syncCaldav } from '../../services/caldav';
import { calendarKeys, setCalendarRole } from '../../services/calendar';
import { compactInput, dangerButton, outlineButton, primaryButton } from '../../components/styles';
import CalendarRoleList from './CalendarRoleList';

/**
 * Connect the user's own CalDAV server (Fastmail, iCloud, Nextcloud, a
 * self-hosted one), give each calendar its role, sync, and disconnect.
 */
export default function CaldavSection() {
  const queryClient = useQueryClient();
  const connection = useQuery({ queryKey: caldavKeys.connection, queryFn: caldavConnection });
  const connected = connection.data ?? null;
  const calendars = useQuery({ queryKey: caldavKeys.calendars, queryFn: caldavCalendars, enabled: connected !== null });
  // CalDAV's calendars feed every calendar view, so a change refreshes them all.
  const refresh = () => queryClient.invalidateQueries({ queryKey: calendarKeys.all });

  const connect = useMutation({ mutationFn: connectCaldav, onSuccess: refresh });
  const sync = useMutation({ mutationFn: syncCaldav, onSettled: refresh });
  const setRole = useMutation({
    mutationFn: ({ id, role }: { id: string; role: CalendarRole }) => setCalendarRole(id, role),
    // A calendar just turned on is synced straight away.
    onSuccess: async (_, { role }) => {
      await refresh();
      if (role !== 'off') sync.mutate();
    },
  });
  const disconnect = useMutation({ mutationFn: disconnectCaldav, onSuccess: refresh });

  const busy = connect.isPending || sync.isPending || disconnect.isPending;
  const failure = [connect, sync, setRole, disconnect].find((m) => m.isError)?.error;
  const showForm = connection.isSuccess && (connected === null || connected.needs_reconnect);

  return (
    <section className="mb-8" aria-labelledby="caldav-heading">
      <h2 id="caldav-heading" className="text-lg font-semibold text-on-surface mb-4 flex items-center gap-2">
        <span className="text-xl">🗓️</span>
        Calendar server (CalDAV)
      </h2>
      <div className="bg-surface border border-outline/20 rounded-lg p-4 sm:p-6 space-y-4">
        {connection.isError && <p className="text-sm text-error">Could not load your CalDAV connection: {errorMessage(connection.error)}</p>}
        {connection.isPending && <p className="text-sm text-on-surface-variant">Loading…</p>}

        {connected && (
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="font-medium text-on-surface break-words">
                Connected to {connected.server_url} as {connected.username}
              </p>
              <p className="text-sm text-on-surface-variant">
                {connected.last_sync_at ? `Last synced ${new Date(connected.last_sync_at).toLocaleString()}` : 'Not synced yet'}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button type="button" onClick={() => sync.mutate()} disabled={busy || connected.needs_reconnect} className={outlineButton}>
                {sync.isPending ? 'Syncing…' : 'Sync now'}
              </button>
              <button type="button" onClick={() => disconnect.mutate()} disabled={busy} className={dangerButton}>
                Disconnect
              </button>
            </div>
          </div>
        )}

        {connected?.needs_reconnect && (
          <p role="status" className="text-sm text-error">
            Your server no longer accepts the saved password. Enter it again to keep syncing.
          </p>
        )}
        {connected && !connected.needs_reconnect && connected.last_error && (
          <p className="text-sm text-error">The last sync failed: {connected.last_error}</p>
        )}

        {showForm && <ConnectForm defaults={connected} busy={busy} onSubmit={(input) => connect.mutate(input)} />}

        {connected && !connected.needs_reconnect && (
          <div>
            <h3 className="font-medium mb-1">Calendars</h3>
            <p className="text-sm text-on-surface-variant mb-3">
              Block: its events make that time busy. FYI: shown, never busy. Mixed: busy unless you mark an event not busy.
            </p>
            {calendars.isError && <p className="text-sm text-error">Could not load calendars: {errorMessage(calendars.error)}</p>}
            {calendars.data && calendars.data.length === 0 && (
              <p className="text-sm text-on-surface-variant">No calendars yet. Sync now to read them from your server.</p>
            )}
            <CalendarRoleList
              calendars={calendars.data ?? []}
              disabled={setRole.isPending}
              onChange={(id, role) => setRole.mutate({ id, role })}
            />
          </div>
        )}

        {connected && (
          <p className="text-xs text-on-surface-variant">
            Cerebro only reads your calendars. Disconnecting deletes the saved password, your calendars and their events from Cerebro.
          </p>
        )}

        {failure !== undefined && <p role="alert" className="text-sm text-error">{errorMessage(failure)}</p>}
      </div>
    </section>
  );
}

/** The server address, user name and password, and a Cloudflare Access service token for a server behind Access. */
function ConnectForm({
  defaults,
  busy,
  onSubmit,
}: {
  defaults: { server_url: string; username: string } | null;
  busy: boolean;
  onSubmit: (input: { server_url: string; username: string; password: string; access_client_id?: string; access_client_secret?: string }) => void;
}) {
  const [serverUrl, setServerUrl] = useState(defaults?.server_url ?? '');
  const [username, setUsername] = useState(defaults?.username ?? '');
  const [password, setPassword] = useState('');
  const [accessId, setAccessId] = useState('');
  const [accessSecret, setAccessSecret] = useState('');

  const submit = (e: FormEvent) => {
    e.preventDefault();
    onSubmit({
      server_url: serverUrl,
      username,
      password,
      ...(accessId || accessSecret ? { access_client_id: accessId, access_client_secret: accessSecret } : {}),
    });
  };

  return (
    <form onSubmit={submit} className="space-y-3">
      {!defaults && (
        <p className="text-on-surface-variant">
          Connect your own calendar server to see its calendars on Today and plan around busy time. Use its CalDAV address
          (Fastmail, iCloud, Nextcloud and self-hosted servers all have one) and an app password where your provider offers one.
          Cerebro only reads; it never changes your calendar.
        </p>
      )}
      <label className="block text-sm">
        Server address
        <input type="url" required value={serverUrl} onChange={(e) => setServerUrl(e.target.value)} placeholder="https://dav.example.com/you/"
          autoComplete="url" className={compactInput} />
      </label>
      <label className="block text-sm">
        User name
        <input required value={username} onChange={(e) => setUsername(e.target.value)} autoComplete="username" className={compactInput} />
      </label>
      <label className="block text-sm">
        Password
        <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password"
          className={compactInput} />
      </label>
      <details className="text-sm">
        <summary className="cursor-pointer text-on-surface-variant">Server behind Cloudflare Access</summary>
        <div className="mt-2 space-y-3">
          <label className="block">
            Access client id
            <input value={accessId} onChange={(e) => setAccessId(e.target.value)} autoComplete="off" className={compactInput} />
          </label>
          <label className="block">
            Access client secret
            <input type="password" value={accessSecret} onChange={(e) => setAccessSecret(e.target.value)} autoComplete="off"
              className={compactInput} />
          </label>
        </div>
      </details>
      <button type="submit" disabled={busy} className={primaryButton}>
        {busy ? 'Connecting…' : defaults ? 'Connect again' : 'Connect calendar server'}
      </button>
    </form>
  );
}
